import { PurchaseOrderGet } from "@kase/my-marathon-communication";
import { ReadonlySignal, computed, signal } from "@preact/signals";
import { ApiSliceParams } from "../interfaces";
import { createListController } from "../utils";
import {
    PurchaseOrder,
    PurchaseOrderItemLine,
    PurchaseOrderLine,
    PurchaseOrderTextLine,
} from "./interfaces";

export const makePurchase = ({ connection, company }: ApiSliceParams) => {
    const ordersStore: Record<string, ReadonlySignal<PurchaseOrder[]>> = {};

    const open = async (order: PurchaseOrder) => {
        const con = connection.value;

        if (!con) {
            return;
        }

        order.loading.value = true;

        for await (const id of con.purchaseorder.lines.list({
            company: company.value,
            po: order._id,
        })) {
            for await (const item of con.purchaseorder.lines.get({
                company: company.value,
                id,
            })) {
                order.lines.value = [
                    ...order.lines.value,
                    {
                        ...item,
                        id: item.id.toString(),
                        _id: item.id,
                    },
                ];
            }
        }

        order.loading.value = false;
    };

    const view = (order: PurchaseOrderGet) => {
        const lines = signal<PurchaseOrderLine[]>([]);

        const finalOrder: PurchaseOrder = {
            ...order,
            id: order.id.toString(),
            _id: order.id,
            loading: signal(false),
            lines,
            items: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderItemLine =>
                        line.line_type?.$case === "item",
                ),
            ),
            texts: computed(() =>
                lines.value.filter(
                    (line): line is PurchaseOrderTextLine =>
                        line.line_type?.$case === "text",
                ),
            ),
        };

        open(finalOrder);

        return finalOrder;
    };

    const {
        items: orders,
        allLoading: loading,
        allLoaded: loaded,
        get,
        listen,
    } = createListController<PurchaseOrderGet, PurchaseOrder>({
        name: "purchaseorder",
        connection,
        list: (con) => con.purchaseorder.items({ company: company.value }),
        view,
    });

    const projectOrders = (id: string) => {
        if (!(id in ordersStore)) {
            ordersStore[id] = computed(() =>
                orders.value.filter(
                    (order) => order.project?.project.toString() === id,
                ),
            );
        }

        if (!loading.peek() && !loaded.peek()) {
            listen();
        }

        return ordersStore[id];
    };

    return {
        orders,
        loading,
        projectOrders,
        listen,
        get,
    };
};
